import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import UploadZone from '@/components/upload/UploadZone';
import VideoPreview from '@/components/upload/VideoPreview';
import UploadProgress from '@/components/upload/UploadProgress';
import ErrorDisplay from '@/components/shared/ErrorDisplay';
import { useVideoUpload } from '@/hooks/useVideoUpload';
import { createJob } from '@/api/resultsApi';

/**
 * HomePage — Landing page con upload video e avvio analisi.
 * Flusso:
 *  1. Seleziona file (UploadZone).
 *  2. Mostra anteprima e bottone "Carica e Analizza".
 *  3. Esegue upload con progresso, poi crea il job di elaborazione.
 *  4. Reindirizza alla pagina della partita (/match/:videoId).
 */
const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { upload, progress, uploading, error: uploadError, videoId, reset: resetUpload } = useVideoUpload();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [jobError, setJobError] = useState<string | null>(null);
  const [creatingJob, setCreatingJob] = useState(false);

  const handleFileSelect = useCallback(
    (file: File) => {
      setSelectedFile(file);
      resetUpload();
      setJobError(null);
    },
    [resetUpload]
  );

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    resetUpload();
    setJobError(null);
  }, [resetUpload]);

  const handleStartAnalysis = useCallback(async () => {
    if (!selectedFile) return;
    setJobError(null);

    // 1. Upload del video
    const vId = await upload(selectedFile);
    if (!vId) return; // Errore già gestito da useVideoUpload

    // 2. Crea il job di elaborazione
    setCreatingJob(true);
    try {
      await createJob(vId);
      // 3. Vai alla pagina partita
      navigate(`/match/${vId}`);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Impossibile avviare l’elaborazione.';
      setJobError(msg);
    } finally {
      setCreatingJob(false);
    }
  }, [selectedFile, upload, navigate]);

  const isProcessing = uploading || creatingJob;

  return (
    <div style={{ maxWidth: '800px', margin: '2rem auto', padding: '0 1rem' }}>
      <h1 style={{ textAlign: 'center', color: '#1e293b', marginBottom: '1.5rem' }}>
        Analizza la tua partita di pallavolo
      </h1>
      <p style={{ textAlign: 'center', color: '#475569', marginBottom: '2rem', lineHeight: 1.5 }}>
        Carica un video e ottieni automaticamente eventi, clip e statistiche.
        <br />
        Nessuna configurazione necessaria — risultati pronti in pochi minuti.
      </p>

      {/* Zona upload (solo se nessun file è selezionato e non stiamo processando) */}
      {!selectedFile && !isProcessing && (
        <UploadZone onFileSelect={handleFileSelect} disabled={false} />
      )}

      {/* Anteprima e azione (file selezionato, nessun processamento in corso) */}
      {selectedFile && !isProcessing && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
          <VideoPreview file={selectedFile} onRemove={handleRemoveFile} />
          <button
            onClick={handleStartAnalysis}
            style={{
              padding: '0.75rem 2.5rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(59,130,246,0.35)',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#2563eb')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#3b82f6')}
          >
            Carica e Analizza
          </button>
        </div>
      )}

      {/* Barra di progresso upload */}
      {uploading && (
        <UploadProgress
          progress={progress}
          status={progress < 100 ? 'Caricamento video in corso…' : 'Caricamento completato! Avvio analisi…'}
        />
      )}

      {/* Indicatore creazione job */}
      {creatingJob && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#475569' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e5e7eb',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              animation: 'vspot-spin 0.8s linear infinite',
              margin: '0 auto 1rem',
            }}
          />
          <p>Avvio elaborazione in corso…</p>
        </div>
      )}

      {/* Errori */}
      {uploadError && (
        <ErrorDisplay
          message={`Errore durante l’upload: ${uploadError}`}
          onRetry={() => {
            resetUpload();
            setSelectedFile(null);
            setJobError(null);
          }}
        />
      )}

      {jobError && (
        <ErrorDisplay
          message={jobError}
          onRetry={() => handleStartAnalysis()}
        />
      )}
    </div>
  );
};

export default HomePage;